import { SaleRow } from "@/models/base";
import { SaleEditState } from "./sale-edit-state";

// Handles selecting multiple sale rows as a block and the clipboard-style
// actions (duplicate / cut / copy / paste / remove) that operate on them.
// A multi-selection never coexists with the single edited row (state.selectedRow):
// the edit form can only ever receive one row, so multi-selecting clears it.
export class SaleEditRowSelection {
	public selectedRows: SaleRow[] = [];
	private multiSelectAnchorIndex: number = -1;
	private static readonly ROW_CLIPBOARD_KEY = "sale-row-clipboard";
	private state: SaleEditState;

	public constructor(state: SaleEditState) {
		this.state = state;
	}

	private resetSelectedRow() {
		this.state.selectedRow = new SaleRow();
		this.state.selectedRow.Discount = this.state.sale.Contact?.Discount || 0;
	}

	public isRowMultiSelected(row: SaleRow): boolean {
		return this.selectedRows.includes(row);
	}

	public clear() {
		if (this.selectedRows.length) {
			this.selectedRows = [];
		}
		this.multiSelectAnchorIndex = -1;
	}

	public toggle(row: SaleRow) {
		this.resetSelectedRow();
		let i = this.selectedRows.indexOf(row);
		if (i == -1) {
			this.selectedRows.push(row);
			this.multiSelectAnchorIndex = this.state.sale.Rows.indexOf(row);
		} else {
			this.selectedRows.splice(i, 1);
			if (this.selectedRows.length == 0) {
				this.multiSelectAnchorIndex = -1;
			}
		}
	}

	public selectRange(row: SaleRow) {
		let rows = this.state.sale.Rows;
		let targetIndex = rows.indexOf(row);
		if (targetIndex == -1) return;
		let anchor = this.multiSelectAnchorIndex;
		if (anchor == -1) {
			anchor = rows.indexOf(this.state.selectedRow);
		}
		if (anchor == -1) {
			anchor = targetIndex;
		}
		this.resetSelectedRow();
		let start = Math.min(anchor, targetIndex);
		let end = Math.max(anchor, targetIndex);
		let range: SaleRow[] = [];
		for (let i = start; i <= end; i++) {
			range.push(rows[i]);
		}
		this.selectedRows = range;
		this.multiSelectAnchorIndex = anchor;
	}

	// The rows currently targeted by a block action: the multi-selection if any,
	// otherwise the single edited row, otherwise none.
	private getActionRows(): SaleRow[] {
		if (this.selectedRows.length > 0) {
			return this.state.sale.Rows.filter((r: SaleRow) => this.selectedRows.includes(r));
		}
		if (this.state.selectedRow && this.state.sale.Rows.includes(this.state.selectedRow)) {
			return [this.state.selectedRow];
		}
		return [];
	}

	// Index right after the last selected row, or the end of the document when
	// nothing is selected.
	private getInsertIndexAfterSelection(): number {
		let rows = this.getActionRows();
		if (rows.length > 0) {
			return this.state.sale.Rows.indexOf(rows[rows.length - 1]) + 1;
		}
		return this.state.sale.Rows.length;
	}

	private buildInsertRow(json: any): SaleRow {
		let copy = {...json, SaleRowConfig: {...json.SaleRowConfig}};
		copy.ID = 0;
		copy.ExtraFieldsID = null;
		return new SaleRow(copy);
	}

	private collectRowsWithTaxRows(rows: SaleRow[]): Set<SaleRow> {
		let allRows = this.state.sale.Rows;
		let result = new Set<SaleRow>();
		for (let row of rows) {
			let i = allRows.indexOf(row);
			if (i == -1) continue;
			result.add(row);
			for (let j = i + 1; j < allRows.length; j++) {
				if (!allRows[j].IsTaxRow) break;
				result.add(allRows[j]);
			}
		}
		return result;
	}

	private reindexDisplayOrder() {
		this.state.sale.Rows.forEach((r: SaleRow, i: number) => {
			r.DisplayOrder = i;
		});
	}

	private async recalcAfterRowsChange() {
		await this.state.processFavv();
		this.state.sale.calculateTotals();
		await this.state.runAutoSaleRowSettings();
		this.state.processFostplus();
	}

	public async duplicate() {
		let toDuplicate = this.getActionRows();
		if (toDuplicate.length == 0) return;
		let insertIndex = this.state.sale.Rows.indexOf(toDuplicate[toDuplicate.length - 1]) + 1;
		let copies = toDuplicate.map((row: SaleRow) => this.buildInsertRow(row.getJSON({includeProduct: true})));
		this.state.sale.Rows.splice(insertIndex, 0, ...copies);
		this.reindexDisplayOrder();
		this.clear();
		this.resetSelectedRow();
		await this.recalcAfterRowsChange();
	}

	public async remove() {
		let toRemove = this.collectRowsWithTaxRows(this.getActionRows());
		if (toRemove.size == 0) return;
		this.state.sale.Rows = this.state.sale.Rows.filter((r: SaleRow) => !toRemove.has(r));
		this.reindexDisplayOrder();
		this.clear();
		this.resetSelectedRow();
		await this.recalcAfterRowsChange();
	}

	// The clipboard lives in localStorage so copy/cut in one sale document can be
	// pasted into another (a new document gets a fresh SaleEditState instance).
	private getClipboard(): any[] {
		let raw = localStorage.getItem(SaleEditRowSelection.ROW_CLIPBOARD_KEY);
		if (!raw) return [];
		try {
			let parsed = JSON.parse(raw);
			return Array.isArray(parsed) ? parsed : [];
		} catch (e) {
			return [];
		}
	}

	private setClipboard(rows: any[]) {
		localStorage.setItem(SaleEditRowSelection.ROW_CLIPBOARD_KEY, JSON.stringify(rows));
	}

	public copy() {
		let toCopy = this.getActionRows();
		if (toCopy.length == 0) return;
		this.setClipboard(toCopy.map((r: SaleRow) => r.getJSON({includeProduct: true})));
	}

	public async cut() {
		let toCut = this.getActionRows();
		if (toCut.length == 0) return;
		this.setClipboard(toCut.map((r: SaleRow) => r.getJSON({includeProduct: true})));
		let toRemove = this.collectRowsWithTaxRows(toCut);
		this.state.sale.Rows = this.state.sale.Rows.filter((r: SaleRow) => !toRemove.has(r));
		this.reindexDisplayOrder();
		this.clear();
		this.resetSelectedRow();
		await this.recalcAfterRowsChange();
	}

	public async paste() {
		let clipboard = this.getClipboard();
		if (clipboard.length == 0) return;
		let insertIndex = this.getInsertIndexAfterSelection();
		let copies = clipboard.map((json: any) => this.buildInsertRow(json));
		this.state.sale.Rows.splice(insertIndex, 0, ...copies);
		this.reindexDisplayOrder();
		this.clear();
		this.resetSelectedRow();
		await this.recalcAfterRowsChange();
	}
}
